/**
 * Page transition wrapper using framer-motion.
 * Provides consistent fade + slide animation for page content.
 */

import { motion, type Variants } from 'framer-motion';
import type { ReactNode } from 'react';

interface PageTransitionProps {
  children: ReactNode;
  /** Animation variant */
  variant?: 'fade' | 'slideUp' | 'slideRight' | 'scale';
}

const fadeVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const slideUpVariants: Variants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

const slideRightVariants: Variants = {
  initial: { opacity: 0, x: -16 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 16 },
};

const scaleVariants: Variants = {
  initial: { opacity: 0, scale: 0.98 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.98 },
};

const variantMap = {
  fade: fadeVariants,
  slideUp: slideUpVariants,
  slideRight: slideRightVariants,
  scale: scaleVariants,
};

/**
 * Page transition component that wraps content with framer-motion animations.
 */
export const PageTransition = ({ children, variant = 'slideUp' }: PageTransitionProps) => {
  return (
    <motion.div
      variants={variantMap[variant]}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{
        duration: 0.25,
        ease: [0.4, 0, 0.2, 1],
      }}
    >
      {children}
    </motion.div>
  );
};

/**
 * Stagger container for list items animation.
 */
export const StaggerContainer = ({ children, stagger = 0.05 }: { children: ReactNode; stagger?: number }) => {
  return (
    <motion.div
      variants={{
        animate: {
          transition: {
            staggerChildren: stagger,
          },
        },
      }}
      initial="initial"
      animate="animate"
    >
      {children}
    </motion.div>
  );
};

/**
 * Individual stagger item.
 */
export const StaggerItem = ({ children }: { children: ReactNode }) => {
  return (
    <motion.div
      variants={{
        initial: { opacity: 0, y: 8 },
        animate: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
        },
      }}
    >
      {children}
    </motion.div>
  );
};

export default PageTransition;
